import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Star, Zap, Leaf, Award, ShoppingBag, TrendingUp, User, Camera, Droplets } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Hero } from '@/components/Hero';
import { DynamicLogo } from '@/components/DynamicLogo';
import { SEO } from '@/components/SEO';
import { ShopStyleProductCard } from '@/components/ShopStyleProductCard';
import { getBestsellers, getNewArrivals, Product } from '@/data/products';
import { WooCommerceProducts, convertWooProductToLocal } from '@/services/woocommerce';
import { useShop } from '@/context/ShopContext';

const homepageSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://marplecompany.co.za/#organization",
      "name": "Marple & Co",
      "url": "https://marplecompany.co.za",
      "logo": "https://marplecompany.co.za/logo.png",
      "description": "African holistic skincare brand founded in 2011. Handmade in Modderfontein, Edenvale, South Africa.",
      "foundingDate": "2011",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Modderfontein",
        "addressRegion": "Edenvale, Gauteng",
        "addressCountry": "ZA"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "email": "hello@marplecompany.co.za",
        "telephone": "+27123456789",
        "contactType": "customer service"
      },
      "sameAs": [
        "https://www.instagram.com/marpleandco",
        "https://www.facebook.com/marpleandco"
      ]
    },
    {
      "@type": "WebSite",
      "@id": "https://marplecompany.co.za/#website",
      "url": "https://marplecompany.co.za",
      "name": "Marple & Co",
      "publisher": { "@id": "https://marplecompany.co.za/#organization" },
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": "https://marplecompany.co.za/shop?search={search_term_string}"
        },
        "query-input": "required name=search_term_string"
      }
    }
  ]
};

const benefits = [
  {
    icon: Zap,
    title: 'Handmade with Love',
    description: 'Each product is carefully crafted by hand in small batches for premium quality.',
    color: '#F4C95D',
  },
  {
    icon: Leaf,
    title: 'African Botanicals',
    description: 'Powered by indigenous African ingredients known for their healing properties.',
    color: '#BDD630',
  },
  {
    icon: Award,
    title: 'All Natural',
    description: 'Free from parabens, sulfates, and petroleum. Pure ingredients only.',
    color: '#567D38',
  },
  {
    icon: Star,
    title: 'Made in South Africa',
    description: 'Proudly crafted in Modderfontein, Edenvale with local expertise.',
    color: '#D9B38C',
  },
];

const CATEGORY_COLORS: Record<string, string> = {
  candles: '#42C5E7',
  tea: '#BDD630',
  'body-care': '#F4C95D',
  skincare: '#D9B38C',
};

function getCategoryColor(category: string) {
  return CATEGORY_COLORS[category] || '#567D38';
}

const journeySteps = [
  { icon: User, title: 'Profile', description: 'Tell us about your skin, climate and budget' },
  { icon: Camera, title: 'Photos', description: 'Front and 45° angles, good even lighting' },
  { icon: Sparkles, title: 'AI Analysis', description: 'Leroy reads your skin in minutes' },
  { icon: ShoppingBag, title: 'Regimen', description: 'A ritual built around what you showed us' },
  { icon: TrendingUp, title: 'Progress', description: 'Track your skin, week after week' },
];

// The three real Marple ranges. Each links into the shop filtered to its
// products rather than a dead marketing page.
const ranges = [
  {
    name: 'Équilibre',
    tagline: 'Barrier & balance',
    description: 'Gentle daily heroes that calm, hydrate and protect sensitive, reactive skin.',
    icon: Leaf,
    tone: '#BDD630',
    query: 'skincare',
  },
  {
    name: 'Anti-Acne',
    tagline: 'Clear & control',
    description: 'Targeted actives that clear congestion, refine pores and steady oil.',
    icon: Droplets,
    tone: '#567D38',
    query: 'acne clarifying',
  },
  {
    name: 'Spatique',
    tagline: 'Renew & glow',
    description: 'Body rituals and facial oils for tone, texture and lit-from-within radiance.',
    icon: Sparkles,
    tone: '#E07A5F',
    query: 'body-care',
  },
];

const shopByConcern = [
  { label: 'Acne & Breakouts', query: 'acne breakouts oil' },
  { label: 'Dark Spots & Pigmentation', query: 'pigmentation dark spots' },
  { label: 'Fine Lines & Wrinkles', query: 'fine lines wrinkles retinal' },
  { label: 'Dryness & Dehydration', query: 'dryness dehydration hydrating' },
  { label: 'Sensitivity & Redness', query: 'sensitivity redness calm' },
  { label: 'Oil Control', query: 'oil control clarifying' },
  { label: 'Large Pores', query: 'pores niacinamide' },
  { label: 'Dullness', query: 'dullness radiance glow' },
];

export function Home() {
  const { addToCart } = useShop();
  const [isVisible, setIsVisible] = useState<Record<string, boolean>>({});
  const [addedToCart, setAddedToCart] = useState<string | null>(null);
  const [featured, setFeatured] = useState<Product[]>([]);
  const [popular, setPopular] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  // Fetch WooCommerce products; fall back to local catalogue
  useEffect(() => {
    async function loadProducts() {
      setLoadingProducts(true);
      try {
        // Best sellers: WooCommerce featured
        const wooFeatured = await WooCommerceProducts.getFeatured(3);
        setFeatured(
          wooFeatured.length > 0
            ? wooFeatured.map(convertWooProductToLocal)
            : getBestsellers().slice(0, 3)
        );

        // Recommended for You: personalised by skin type if available
        const skinType = localStorage.getItem('marple_skin_type');
        let wooRecommended: any[] = [];
        if (skinType) {
          wooRecommended = await WooCommerceProducts.getAll({ tag: skinType, per_page: 3 }).catch(() => []);
        }
        if (!wooRecommended.length) {
          wooRecommended = await WooCommerceProducts.getAll({ orderby: 'rating', per_page: 3 }).catch(() => []);
        }
        setPopular(
          wooRecommended.length > 0
            ? wooRecommended.map(convertWooProductToLocal)
            : getNewArrivals().slice(0, 3)
        );
      } catch {
        setFeatured(getBestsellers().slice(0, 3));
        setPopular(getNewArrivals().slice(0, 3));
      } finally {
        setLoadingProducts(false);
      }
    }
    loadProducts();
  }, []);

  // Intersection observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible((prev) => ({
            ...prev,
            [entry.target.id]: entry.isIntersecting,
          }));
        });
      },
      { threshold: 0.1, rootMargin: '-50px' }
    );

    document.querySelectorAll('[data-animate]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, [loadingProducts]);

  const handleAddToCart = (product: Product) => {
    addToCart(product, 1);
    setAddedToCart(product.id);
    setTimeout(() => setAddedToCart(null), 2000);
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Marple & Co | African Holistic Skincare — Root-to-Radiance"
        description="Handmade African botanical skincare from Modderfontein, South Africa. Topical, ingestible and supported — skincare from the inside out. Shop Spatique body care, herbal wellness teas, and aromatherapy candles."
        path="/"
        schema={homepageSchema}
      />
      {/* Hero */}
      <Hero />

      {/* ── Skin Journey ──────────────────────────────────────────────── */}
      <section id="journey" data-animate className="py-16 bg-white border-b" style={{ borderColor: '#F0EBE3' }}>
        <div className="container-marple">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-widest mb-3 block" style={{ color: '#E07A5F' }}>The Skin Journey</span>
            <h2 className="font-serif normal-case text-3xl sm:text-4xl font-bold" style={{ color: '#567D38' }}>Five steps to skin that fits you</h2>
          </div>
          <div
            className={cn(
              'grid sm:grid-cols-2 lg:grid-cols-5 gap-8 transition-all duration-700',
              isVisible.journey ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            )}
          >
            {journeySteps.map((step, index) => (
              <div key={step.title} className="flex flex-col items-center text-center gap-3">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center relative border-2"
                  style={{ background: '#fff', borderColor: '#BDD630' }}
                >
                  <step.icon className="w-6 h-6" style={{ color: '#567D38' }} />
                </div>
                <h3 className="font-display font-bold text-sm uppercase tracking-tight" style={{ color: '#1A2E12' }}>
                  {index + 1}. {step.title}
                </h3>
                <p className="text-xs leading-relaxed" style={{ color: '#6B7B6E' }}>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Our Ranges ───────────────────────────────────────────────────── */}
      <section id="ranges" data-animate className="py-16" style={{ background: '#F7F5EE' }}>
        <div className="container-marple">
          <div className="text-center mb-10">
            <span className="text-xs font-bold uppercase tracking-widest mb-3 block" style={{ color: '#E07A5F' }}>Our Ranges</span>
            <h2 className="font-serif normal-case text-3xl sm:text-4xl font-bold" style={{ color: '#567D38' }}>
              Three ways to meet your skin
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {ranges.map((r) => (
              <Link
                key={r.name}
                to={`/shop?search=${encodeURIComponent(r.query)}`}
                className="group bg-white rounded-2xl overflow-hidden flex flex-col transition-all duration-300 hover:shadow-xl"
                style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
              >
                <div style={{ height: 8, background: r.tone }} />
                <div className="p-6 flex flex-col flex-1">
                  <div
                    className="w-13 h-13 rounded-xl flex items-center justify-center mb-4"
                    style={{ width: 52, height: 52, background: r.tone }}
                  >
                    <r.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-[11px] font-bold uppercase tracking-widest" style={{ color: '#E07A5F' }}>
                    {r.tagline}
                  </div>
                  <h3 className="font-serif normal-case text-2xl font-bold mt-1 mb-2.5" style={{ color: '#567D38' }}>
                    {r.name}
                  </h3>
                  <p className="text-sm leading-relaxed flex-1" style={{ color: '#6B7B6E' }}>
                    {r.description}
                  </p>
                  <span
                    className="inline-flex items-center gap-1.5 mt-4 text-xs font-bold uppercase tracking-wider"
                    style={{ color: '#567D38' }}
                  >
                    Explore <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Shop by Concern ──────────────────────────────────────────────── */}
      <section id="concern" data-animate className="py-16 bg-white">
        <div className="container-marple">
          <div className="text-center mb-10">
            <span className="text-xs font-bold uppercase tracking-widest mb-3 block" style={{ color: '#E07A5F' }}>Shop by Concern</span>
            <h2 className="font-serif normal-case text-3xl sm:text-4xl font-bold" style={{ color: '#567D38' }}>What's your skin telling you?</h2>
          </div>
          <div
            className={cn(
              'grid sm:grid-cols-2 lg:grid-cols-4 gap-3.5 transition-all duration-700',
              isVisible.concern ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            )}
          >
            {shopByConcern.map((c) => (
              <Link
                key={c.label}
                to={`/shop?search=${encodeURIComponent(c.query)}`}
                className="flex items-center justify-between px-5 py-4 rounded-xl border transition-colors hover:border-[#567D38]"
                style={{ background: '#F7F5EE', borderColor: '#D9B38C40' }}
              >
                <span className="text-sm font-medium" style={{ color: '#1A2E12' }}>{c.label}</span>
                <ArrowRight className="w-4 h-4" style={{ color: '#567D38' }} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Best Sellers ──────────────────────────────────────────────── */}
      <section id="featured" data-animate className="py-24 bg-white">
        <div className="container-marple">
          <div
            className={cn(
              'text-center mb-16 transition-all duration-700',
              isVisible.featured ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            )}
          >
            <span className="label block mb-3" style={{ color: '#BDD630' }}>Loved by the Collective</span>
            <h2 className="heading-2 mb-4" style={{ color: '#1A2E12' }}>THE CULT SHELF</h2>
            <p className="text-elegant max-w-xl mx-auto" style={{ color: '#6B7B6E' }}>
              Our most-loved formulas, chosen by thousands of customers across South Africa.
            </p>
          </div>

          {loadingProducts ? (
            <ProductSkeletons count={3} />
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {featured.map((product, index) => (
                <ShopStyleProductCard
                  key={product.id}
                  product={product}
                  index={index}
                  isVisible={isVisible.featured}
                  addedToCart={addedToCart === product.id}
                  onAddToCart={() => handleAddToCart(product)}
                  categoryColor={getCategoryColor(product.category)}
                  forceBadge="BESTSELLER"
                />
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link to="/shop" className="btn btn-secondary btn-large inline-flex items-center gap-2">
              Explore the Full Garden
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Divider ───────────────────────────────────────────────────── */}
      <div className="h-px mx-auto" style={{ maxWidth: '80rem', background: 'linear-gradient(90deg, transparent, #D9B38C, transparent)' }} />

      {/* ── Recommended for You ────────────────────────────────────────── */}
      <section id="popular" data-animate className="py-24" style={{ background: '#F7F5EE' }}>
        <div className="container-marple">
          <div
            className={cn(
              'flex flex-col sm:flex-row sm:items-end sm:justify-between mb-16 gap-4 transition-all duration-700',
              isVisible.popular ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            )}
          >
            <div>
              <span className="label block mb-3 flex items-center gap-2" style={{ color: '#567D38' }}>
                <Sparkles className="w-4 h-4" />
                {localStorage.getItem('marple_skin_type')
                  ? `Matched to your ${localStorage.getItem('marple_skin_type')} skin`
                  : 'The Garden Chose These for You'}
              </span>
              <h2 className="heading-2" style={{ color: '#1A2E12' }}>CHOSEN FOR YOUR SKIN</h2>
            </div>
            <Link to="/shop" className="btn btn-secondary hidden sm:flex items-center gap-2">
              See the Full Harvest
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loadingProducts ? (
            <ProductSkeletons count={3} />
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {popular.map((product, index) => (
                <ShopStyleProductCard
                  key={product.id}
                  product={product}
                  index={index}
                  isVisible={isVisible.popular}
                  addedToCart={addedToCart === product.id}
                  onAddToCart={() => handleAddToCart(product)}
                  categoryColor={getCategoryColor(product.category)}
                  forceBadge="THE GARDEN CHOSE THIS FOR YOU"
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── Category Showcase ─────────────────────────────────────────── */}
      <section id="categories" data-animate className="py-24 relative overflow-hidden" style={{ background: '#1A2E12' }}>
        <div className="absolute inset-0 pattern-octagon text-white/5" />

        <div className="container-marple relative z-10">
          <div className="text-center mb-16">
            <span className="label block mb-3" style={{ color: '#BDD630' }}>From the Marple Garden</span>
            <h2 className="heading-2 text-white">EXPLORE THE GARDEN</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: 'Gel Candles',
                description: 'Handmade aromatherapy candles with real botanicals',
                color: '#42C5E7',
                link: '/shop?category=candles',
                emoji: '🕯️',
              },
              {
                title: 'Herbal Teas',
                description: 'Wellness blends from ancient African traditions',
                color: '#BDD630',
                link: '/shop?category=tea',
                emoji: '🍵',
              },
              {
                title: 'Body Care',
                description: 'Luxurious spatique range for radiant skin',
                color: '#F4C95D',
                link: '/shop?category=body-care',
                emoji: '✨',
              },
              {
                title: 'Skin Care',
                description: 'Premium formulas for your daily ritual',
                color: '#D9B38C',
                link: '/shop?category=skincare',
                emoji: '🌿',
              },
            ].map((cat, index) => (
              <Link
                key={cat.title}
                to={cat.link}
                className={cn(
                  'group relative h-72 overflow-hidden flex flex-col justify-end p-8 transition-all duration-700',
                  isVisible.categories ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                )}
                style={{
                  transitionDelay: `${index * 100}ms`,
                  background: `${cat.color}18`,
                  border: `1px solid ${cat.color}30`,
                }}
              >
                {/* Top accent line */}
                <div
                  className="absolute top-0 left-0 right-0 h-1 transition-opacity duration-300 opacity-0 group-hover:opacity-100"
                  style={{ background: cat.color }}
                />
                {/* Emoji decoration */}
                <div className="text-5xl mb-4 transition-transform duration-300 group-hover:scale-110 select-none">
                  {cat.emoji}
                </div>
                <h3 className="font-display font-bold text-lg text-white uppercase tracking-tight mb-2">
                  {cat.title}
                </h3>
                <p className="text-white/60 text-sm mb-4">{cat.description}</p>
                <span
                  className="label text-xs flex items-center gap-1 transition-gap duration-200"
                  style={{ color: cat.color }}
                >
                  Explore
                  <ArrowRight className="w-3 h-3 transition-transform duration-200 group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Benefits ──────────────────────────────────────────────────── */}
      <section id="benefits" data-animate className="py-24 bg-white">
        <div className="container-marple">
          <div className="text-center mb-16">
            <span className="label block mb-3" style={{ color: '#567D38' }}>The Marple Way</span>
            <h2 className="heading-2" style={{ color: '#1A2E12' }}>WHY OUR RITUALS WORK</h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={benefit.title}
                className={cn(
                  'group p-8 border hover:shadow-lg transition-all duration-500',
                  isVisible.benefits ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                )}
                style={{
                  borderColor: '#F0EBE3',
                  transitionDelay: `${index * 100}ms`,
                }}
              >
                <div
                  className="w-14 h-14 mb-6 flex items-center justify-center transition-transform duration-300 group-hover:scale-110 rounded-sm"
                  style={{ backgroundColor: `${benefit.color}15` }}
                >
                  <benefit.icon className="w-7 h-7" style={{ color: benefit.color }} />
                </div>
                <h3 className="font-display font-bold text-base uppercase tracking-tight mb-3" style={{ color: '#1A2E12' }}>
                  {benefit.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: '#6B7B6E' }}>
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Member Portal CTA ─────────────────────────────────────────── */}
      <section id="member-cta" data-animate className="py-24 relative overflow-hidden" style={{ background: '#BDD630' }}>
        <div className="absolute inset-0 pattern-octagon opacity-10" />

        <div className="container-marple relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="label block mb-4" style={{ color: '#567D38' }}>Your Ritual Sanctuary</span>
              <h2 className="heading-2 mb-6" style={{ color: '#1A2E12' }}>
                YOUR SKIN'S PRIVATE<br />
                <span style={{ color: '#567D38' }}>SANCTUARY</span>
              </h2>
              <p className="font-elegant italic text-xl mb-8" style={{ color: '#567D38' }}>
                Your skin deserves a personalised path. Join free to unlock your ritual analysis,
                curated product shelf, and a community that glows together.
              </p>
              <ul className="space-y-3 mb-10">
                {[
                  'AI-powered skin analysis',
                  'Personalized product recommendations',
                  'Track your skin progress over time',
                  'Exclusive member-only offers',
                  'Skincare routine reminders',
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3" style={{ color: '#1A2E12' }}>
                    <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-full" style={{ background: '#567D38' }}>
                      <Star className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-sm font-medium">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/member/create-account"
                  className="btn btn-large inline-flex items-center justify-center gap-2"
                  style={{ background: '#1A2E12', color: 'white' }}
                >
                  Join the Inner Circle
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/member/login"
                  className="btn btn-large btn-secondary inline-flex items-center justify-center gap-2"
                >
                  Sign In
                </Link>
              </div>
            </div>

            <div className="relative hidden lg:block">
              <div
                className="aspect-square flex items-center justify-center p-16"
                style={{ background: '#96c23d' }}
              >
                <div className="text-center">
                  <div
                    className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center"
                    style={{ background: '#1A2E12' }}
                  >
                    <Sparkles className="w-12 h-12" style={{ color: '#BDD630' }} />
                  </div>
                  <h3 className="font-display font-bold text-3xl mb-2" style={{ color: '#1A2E12' }}>
                    10,000+ Members
                  </h3>
                  <p className="font-elegant italic text-lg" style={{ color: '#567D38' }}>
                    Transforming skin, one routine at a time
                  </p>
                </div>
              </div>
              <div className="absolute -bottom-6 -right-6 px-8 py-6 bg-white shadow-2xl">
                <p className="font-display font-bold text-4xl" style={{ color: '#567D38' }}>FREE</p>
                <p className="label text-xs" style={{ color: '#6B7B6E' }}>Forever</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Professional Portal CTA ────────────────────────────────────── */}
      <section className="py-20 relative overflow-hidden" style={{ background: '#1A2E12' }}>
        <div className="container-marple relative z-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-10">
            <div className="max-w-xl">
              <span className="text-xs font-bold tracking-widest uppercase text-[#BDD630] mb-3 block">For Skin & Health Professionals</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                Join our Professional Review Network
              </h2>
              <p className="text-[rgba(255,255,255,0.7)] text-base mb-6 leading-relaxed">
                Dermatologists, dieticians, estheticians and GPs — register free and earn
                <strong className="text-[#BDD630]"> R360 per review</strong>. Access a pipeline of anonymized
                skin assessments and provide evidence-based clinical commentary.
              </p>
              <ul className="space-y-2 mb-8">
                {['Free professional registration', 'Anonymized client reports — no PII', 'AI pre-analysis already done', 'Earn R360 per completed review', 'Wallet-based payouts'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-white/80">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#BDD630] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="flex gap-4">
                <Link to="/professional/register"
                  className="px-6 py-3 bg-[#BDD630] text-[#1A2E12] font-bold rounded-full text-sm hover:bg-[#97c23c] transition-colors">
                  Register for Free
                </Link>
                <Link to="/professional/login"
                  className="px-6 py-3 border border-white/30 text-white font-semibold rounded-full text-sm hover:bg-white/10 transition-colors">
                  Sign In
                </Link>
              </div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-3xl p-8 max-w-xs w-full text-center">
              <div className="w-14 h-14 bg-[#BDD630]/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🩺</span>
              </div>
              <p className="text-white font-bold text-lg mb-2">Earn up to</p>
              <p className="text-[#BDD630] font-black text-5xl mb-2">R360</p>
              <p className="text-white/60 text-sm">per professional review completed</p>
              <div className="border-t border-white/10 mt-6 pt-6 text-left space-y-2">
                <div className="flex justify-between text-xs"><span className="text-white/60">Review fee</span><span className="text-white">R400.00</span></div>
                <div className="flex justify-between text-xs"><span className="text-white/60">Marple commission (10%)</span><span className="text-white/60">- R40.00</span></div>
                <div className="flex justify-between text-xs font-bold border-t border-white/10 pt-2"><span className="text-[#BDD630]">Your payout</span><span className="text-[#BDD630]">R360.00</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Brand Story ───────────────────────────────────────────────── */}
      <section className="py-24 relative overflow-hidden" style={{ background: '#567D38' }}>
        <div className="absolute inset-0 pattern-octagon text-white/5" />

        <div className="container-marple relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="label block mb-4" style={{ color: '#BDD630' }}>Rooted in the Earth</span>
              <h2 className="heading-2 text-white mb-6">
                HANDMADE IN<br />
                <span style={{ color: '#BDD630' }}>SOUTH AFRICA</span>
              </h2>
              <p className="font-elegant italic text-xl mb-6" style={{ color: 'rgba(255,255,255,0.7)' }}>
                Founded in 2011, Marple & Co. has been crafting premium skincare and wellness
                products using the finest African botanicals.
              </p>
              <p className="mb-10 leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>
                Located in Modderfontein, Edenvale, our dedicated team creates each product
                by hand, ensuring the highest quality. We believe in the power of natural
                ingredients and sustainable, ethical practices.
              </p>
              <Link to="/about" className="btn btn-accent inline-flex items-center gap-2">
                Read Our Story
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>

            <div className="relative hidden lg:block">
              <div className="aspect-square flex items-center justify-center p-16" style={{ background: '#1d4a30' }}>
                <div className="text-center">
                  <DynamicLogo variant="white" size="xl" showText />
                  <p className="mt-8 font-elegant italic" style={{ color: 'rgba(255,255,255,0.55)' }}>
                    "Beautiful skin starts within"
                  </p>
                  <p className="mt-2 text-sm" style={{ color: 'rgba(255,255,255,0.3)' }}>EST. 2011</p>
                </div>
              </div>
              <div
                className="absolute -bottom-6 -right-6 px-8 py-6"
                style={{ background: '#BDD630', color: '#567D38' }}
              >
                <p className="font-display font-bold text-4xl">13+</p>
                <p className="label text-xs">Years of Excellence</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Newsletter ────────────────────────────────────────────────── */}
      <section className="py-24" style={{ background: '#F7F5EE' }}>
        <div className="container-marple text-center" style={{ maxWidth: '36rem' }}>
          <DynamicLogo size="md" className="mx-auto mb-6" />
          <h2 className="heading-3 mb-4" style={{ color: '#1A2E12' }}>
            STAY IN THE CIRCLE
          </h2>
          <p className="mb-10 text-base" style={{ color: '#6B7B6E' }}>
            Be first to know when new rituals arrive, get members-only offers, and receive
            skincare wisdom straight to your inbox.
          </p>
          <form
            className="flex flex-col sm:flex-row gap-3"
            onSubmit={(e) => e.preventDefault()}
          >
            <label htmlFor="newsletter-email" className="sr-only">Email address</label>
            <input
              id="newsletter-email"
              type="email"
              placeholder="Let's stay connected — drop your email"
              required
              className="flex-1 px-6 py-4 bg-white border focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors text-sm"
              style={{ borderColor: '#D9B38C', '--tw-ring-color': '#567D38' } as React.CSSProperties}
            />
            <button
              type="submit"
              className="btn btn-primary btn-large whitespace-nowrap"
            >
              Keep Me in the Loop
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

// ─── Skeleton loader for products ─────────────────────────────────────────

function ProductSkeletons({ count }: { count: number }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-white border animate-pulse" style={{ borderColor: '#F0EBE3' }}>
          <div className="aspect-[4/3]" style={{ background: '#F7F5EE' }} />
          <div className="p-5 space-y-3">
            <div className="h-3 rounded w-20" style={{ background: '#F0EBE3' }} />
            <div className="h-4 rounded w-3/4" style={{ background: '#F0EBE3' }} />
            <div className="h-3 rounded w-full" style={{ background: '#F0EBE3' }} />
            <div className="h-3 rounded w-2/3" style={{ background: '#F0EBE3' }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export default Home;
